import React, { useState } from 'react';
import { Box, IconButton, LinearProgress, Stack } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { Rows } from './Rows';
import { TableCell } from './TableCell';
import { TablePagination } from './TablePagination';
import { Column, Group, ListingEvent, StaticSettings } from '../types';

interface Props<Data extends Record<string, any>, Context extends any = any> {
  context: Context;
  groups: Group<Data>[];
  staticSettings?: StaticSettings;
  columns: Column<Data, Context>[];
  onEmit?: (event: ListingEvent, data?: unknown) => void;
}

export const RowsGroup = <Data extends Record<string, any>, Context extends any = any>({
  onEmit,
  groups,
  context,
  columns,
  staticSettings,
}: Props<Data, Context>) => {
  const [openGroups, setIsOpen] = useState<Record<string, boolean>>({});

  // Cumulative offset so each group's rows carry their absolute index in the
  // flat dataset (required for cells that bind to react-hook-form paths).
  let indexOffset = 0;

  return (
    <Box position="relative">
      {groups.map(group => {
        const isOpen = openGroups[group.name] ?? staticSettings?.allGroupsOpen ?? true;
        const groupIndexOffset = indexOffset;
        indexOffset += group.data.length;

        return (
          <Box key={group.name}>
            {group?.isLoading && <LinearProgress color="primary" className="listing-loading" />}

            {group?.beforeGroup && (
              <TableCell context={context} sx={{ ...(group.beforeGroup?.cellStyles ?? {}) }}>
                <Stack width="100%" direction="row" gap="0.2rem" alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setIsOpen(prev => {
                        const nextValue = prev[group.name] === undefined ? false : !prev[group.name];

                        onEmit?.(nextValue ? 'groupOpened' : 'groupClosed', group.name);

                        return { ...prev, [group.name]: nextValue };
                      })
                    }
                  >
                    {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                  </IconButton>

                  {group.beforeGroup.render(group)}
                </Stack>
              </TableCell>
            )}

            {isOpen && (
              <>
                <Rows tableData={group.data} context={context} columns={columns} indexOffset={groupIndexOffset} />

                {group.pagination && <TablePagination pagination={group.pagination} />}

                {group?.afterGroup && group.afterGroup.render(group)}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
